import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { blogAuthorPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityBlogCategory } from '@data/sanity/queries/types/blog'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getBlogAuthorPage } from '@lib/sanity/blog'
import { getPageSanityClient, getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'
import { getEnabledLocales } from '@lib/sanity/site'
import { useDefaultLocaleCanonicalUrl } from '@lib/canonical-url'
import { type Locale } from '@lib/language'
import { getNonNullValues } from '@lib/page'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
} from '@lib/routes'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface BlogAuthorPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  slug: string
  page: SanityBlogCategory
  site: SanitySiteFragment
}

const BlogAuthorPage = ({
  draftMode,
  draftToken,
  locale,
  slug,
  page,
  site,
}: BlogAuthorPageProps) => {
  const canonicalUrl = useDefaultLocaleCanonicalUrl('blogAuthor', page.slug)

  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  if (draftMode && draftToken) {
    return (
      <Layout
        page={page}
        site={site}
        canonicalUrl={canonicalUrl}
        schemas={schemas}
        draftMode
      >
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={blogAuthorPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
              blogAuthorSlug: slug,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
    >
      <Modules modules={page.modules} />
    </Layout>
  )
}

interface BlogAuthorPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogAuthorPageParams>
): Promise<GetStaticPropsResult<BlogAuthorPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const enabledLocales = await getEnabledLocales(sanityClient)
  const slug = context.params?.slug ?? ''
  const blogAuthorPage = await getBlogAuthorPage(
    sanityClient,
    context.locale,
    slug
  )

  if (!blogAuthorPage.page || !enabledLocales.includes(context.locale)) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      slug,
      page: getNonNullValues(blogAuthorPage.page),
      site: getNonNullValues(blogAuthorPage.site),
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogAuthorPageParams>
> {
  const sanityClient = getSanityClient()
  const blogAuthors = await getAllSlugs(sanityClient, 'blogAuthor')
  const enabledLocales = await getEnabledLocales(sanityClient)
  const paths: StaticPathWithSlug[] = []

  blogAuthors
    .filter(({ slug }) => !!slug)
    .forEach(({ slug }) => {
      enabledLocales.forEach((locale) => {
        paths.push({
          params: {
            slug,
          },
          locale: locale as Locale,
        })
      })
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogAuthorPage
